import { z } from 'zod';

export const VOICE_CONVERSATION_HISTORY_ROLES = [
  'user',
  'assistant',
  'system',
] as const;
export type VoiceConversationHistoryRole =
  (typeof VOICE_CONVERSATION_HISTORY_ROLES)[number];

export const VOICE_CONVERSATION_INVALID_RESPONSE_REASONS = [
  'IS_JSON_OBJECT_OR_ARRAY',
  'IS_HTML',
  'IS_XML',
] as const;
export type VoiceConversationInvalidResponseReason =
  (typeof VOICE_CONVERSATION_INVALID_RESPONSE_REASONS)[number];

const VoiceConversationHistoryRoleSchema = z.enum(
  VOICE_CONVERSATION_HISTORY_ROLES
);
const VoiceConversationInvalidResponseReasonSchema = z.enum(
  VOICE_CONVERSATION_INVALID_RESPONSE_REASONS
);

const TimestampSchema = z.preprocess((value) => {
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const parsedDate = new Date(value);
    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate;
    }
  }
  return value;
}, z.date());

const JsonStringSchema = z.string().refine(
  (value) => {
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  },
  { message: 'Expected a valid JSON string' }
);

export const VoiceConversationHistoryWordSchema = z
  .object({
    word: z.string(),
    confidence: z.number().optional(),
    start: TimestampSchema.optional(),
    end: TimestampSchema.optional(),
  })
  .strict();
export type VoiceConversationHistoryWord = z.infer<
  typeof VoiceConversationHistoryWordSchema
>;

const SttModelUsedSchema = z
  .object({ provider: z.string(), model: z.string() })
  .strict();

export type SttModelUsed = z.infer<typeof SttModelUsedSchema>;

const ContributionBaseSchema = z
  .object({
    historyItemId: z.string(),
    createdAt: TimestampSchema,
    role: VoiceConversationHistoryRoleSchema,
    transcript: z.string(),
    wordConfidences: z.array(VoiceConversationHistoryWordSchema).optional(),
    sttModelUsed: SttModelUsedSchema.optional(),
    eventTimestamp: TimestampSchema,
  })
  .strict();

const StartedContributionSchema = ContributionBaseSchema.extend({
  type: z.literal('startedSaying'),
}).strict();

const ContinuedContributionSchema = ContributionBaseSchema.extend({
  type: z.literal('continuedSaying'),
}).strict();

const InterruptedContributionSchema = ContributionBaseSchema.extend({
  type: z.literal('interrupted'),
}).strict();

const FinishedContributionSchema = ContributionBaseSchema.omit({
  transcript: true,
})
  .extend({
    type: z.literal('finishedSaying'),
    transcript: z.string().optional(),
  })
  .strict();

const FunctionBaseSchema = z
  .object({
    historyItemId: z.string(),
    createdAt: TimestampSchema,
    name: z.string(),
    arguments: JsonStringSchema,
    rationale: z.string(),
    eventTimestamp: TimestampSchema,
  })
  .strict();

const FunctionCallSchema = FunctionBaseSchema.extend({
  type: z.literal('functionCall'),
}).strict();

const FunctionReturnSchema = FunctionBaseSchema.extend({
  type: z.literal('functionReturn'),
  output: z.string(),
}).strict();

const ResponseRequestSchema = z
  .object({
    type: z.literal('responseRequest'),
    historyItemId: z.string(),
    eventTimestamp: TimestampSchema,
  })
  .strict();

const InvalidResponseSchema = z
  .object({
    type: z.literal('invalidResponse'),
    response: z.string(),
    reasons: z.array(VoiceConversationInvalidResponseReasonSchema),
    eventTimestamp: TimestampSchema,
  })
  .strict();

const ErrorSchema = z
  .object({
    type: z.literal('error'),
    error: z.string(),
    eventTimestamp: TimestampSchema,
  })
  .strict();

const LogSchema = z
  .object({
    type: z.literal('log'),
    log: z.string(),
    data: z.unknown().optional(),
    eventTimestamp: TimestampSchema,
  })
  .strict();

const SystemUpdateRequestedSchema = z
  .object({
    type: z.literal('systemUpdateRequested'),
    historyItemId: z.string(),
    systemPrompt: z.string(),
    tools: z.array(z.string()),
    eventTimestamp: TimestampSchema,
  })
  .strict();

const SystemUpdateAppliedSchema = z
  .object({
    type: z.literal('systemUpdateApplied'),
    historyItemId: z.string(),
    session: z.record(z.unknown()),
    eventTimestamp: TimestampSchema,
  })
  .strict();

export const VoiceConversationHistoryEventSchema = z.discriminatedUnion(
  'type',
  [
    StartedContributionSchema,
    ContinuedContributionSchema,
    InterruptedContributionSchema,
    FinishedContributionSchema,
    FunctionCallSchema,
    FunctionReturnSchema,
    ResponseRequestSchema,
    InvalidResponseSchema,
    ErrorSchema,
    LogSchema,
    SystemUpdateRequestedSchema,
    SystemUpdateAppliedSchema,
  ]
);

export const VoiceConversationHistoryLogSchema = z.array(
  VoiceConversationHistoryEventSchema
);

export type VoiceConversationHistoryEvent = z.infer<
  typeof VoiceConversationHistoryEventSchema
>;

export type VoiceConversationHistoryEventByType<
  EventType extends VoiceConversationHistoryEvent['type'],
> = Extract<VoiceConversationHistoryEvent, { type: EventType }>;

export type VoiceConversationHistoryItemByType<
  EventType extends VoiceConversationHistoryEvent['type'],
> = Omit<VoiceConversationHistoryEventByType<EventType>, 'eventTimestamp'>;

export type HistoryItemId = string;

export type ContributionRole = VoiceConversationHistoryRole;

export type ContributionItem = Omit<
  VoiceConversationHistoryItemByType<'startedSaying'>,
  'type'
>;

export type StartedContributionHistoryItem =
  VoiceConversationHistoryItemByType<'startedSaying'>;

export type FinishedContributionHistoryItem =
  VoiceConversationHistoryItemByType<'finishedSaying'>;

export type InterruptedContributionHistoryItem =
  VoiceConversationHistoryItemByType<'interrupted'>;

export type ContinuedContributionHistoryItem =
  VoiceConversationHistoryItemByType<'continuedSaying'>;

export type FinalizedContributionHistoryItem =
  | FinishedContributionHistoryItem
  | InterruptedContributionHistoryItem;

export type ContributionHistoryItem =
  | StartedContributionHistoryItem
  | ContinuedContributionHistoryItem
  | FinalizedContributionHistoryItem;

export type FunctionItem = Omit<
  VoiceConversationHistoryItemByType<'functionCall'>,
  'type'
>;

export type FunctionCallHistoryItem =
  VoiceConversationHistoryItemByType<'functionCall'>;

export type FunctionReturnHistoryItem =
  VoiceConversationHistoryItemByType<'functionReturn'>;

export type ResponseRequestHistoryItem =
  VoiceConversationHistoryItemByType<'responseRequest'>;

export type InvalidResponseHistoryItem =
  VoiceConversationHistoryItemByType<'invalidResponse'>;

export type ErrorHistoryItem = VoiceConversationHistoryItemByType<'error'>;

export type LogHistoryItem =
  | ResponseRequestHistoryItem
  | InvalidResponseHistoryItem
  | ErrorHistoryItem
  | VoiceConversationHistoryItemByType<'log'>;

export type SystemUpdateRequestedHistoryItem =
  VoiceConversationHistoryItemByType<'systemUpdateRequested'>;

export type SystemUpdateAppliedHistoryItem =
  VoiceConversationHistoryItemByType<'systemUpdateApplied'>;

export type SystemUpdateHistoryItem =
  | SystemUpdateRequestedHistoryItem
  | SystemUpdateAppliedHistoryItem;

export type FunctionHistoryItem =
  | FunctionCallHistoryItem
  | FunctionReturnHistoryItem;

export type HistoryItem =
  | ContributionHistoryItem
  | FunctionHistoryItem
  | SystemUpdateHistoryItem;

export type StringifiedItem = {
  createdAt: Date;
  role: ContributionRole;
  transcript: string;
};

export type LoggedEvent = VoiceConversationHistoryEvent;
